import React from 'react';
import PropTypes from 'prop-types';
import { X, Check, CalendarClock, AlertCircle } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { formatDateTime, relativeTimeLabel } from './tabFormatters';

// "When" column — Interview tab only (senior_level_flow's Test/Interview/
// In Progress states), off row.interview_details:
//   { panel_names, interview_time, acknowledged_at, rejected_at,
//     requires_acknowledgment, format }
// A status pill (Rejected / Acknowledged / Requires acknowledgment /
// Scheduled) over the interview's date/time and a plain relative-time
// phrase. Precedence, per explicit instruction:
//   rejected_at set    → Rejected
//   acknowledged_at set → Acknowledged
//   requires_acknowledgment → Requires acknowledgment
//   otherwise           → Scheduled
// rejected_at is checked ahead of acknowledged_at — in practice the two
// should never both be set for the same interview, but a rejection is the
// more consequential state to surface if they ever did.
const STATUS_STYLES = {
  rejected: {
    label: 'Rejected',
    Icon: X,
    className: 'bg-ds-danger-subtle text-ds-danger border-ds-danger/25',
  },
  acknowledged: {
    label: 'Acknowledged',
    Icon: Check,
    className: 'bg-ds-success-subtle text-ds-success border-ds-success-tint',
  },
  requires_acknowledgment: {
    label: 'Requires acknowledgment',
    Icon: AlertCircle,
    className: 'bg-ds-warning-subtle text-ds-warning border-ds-warning-tint',
  },
  scheduled: {
    label: 'Scheduled',
    Icon: CalendarClock,
    className: 'bg-brand-bg-subtle text-primary border-primary/20',
  },
};

const interviewStatusFrom = (details) => {
  if (details?.rejected_at) return 'rejected';
  if (details?.acknowledged_at) return 'acknowledged';
  if (details?.requires_acknowledgment) return 'requires_acknowledgment';
  return 'scheduled';
};

const InterviewWhenCell = ({ row }) => {
  const details = row.interview_details;
  if (!details) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const { label, Icon, className } =
    STATUS_STYLES[interviewStatusFrom(details)];
  const dateTime = formatDateTime(details.interview_time);
  const relative = relativeTimeLabel(details.interview_time);

  return (
    <div className="min-w-0">
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-sm border px-2 py-[2px] text-[11.5px] font-semibold whitespace-nowrap',
          className
        )}
      >
        <Icon className="h-3 w-3" strokeWidth={2.5} />
        {label}
      </span>
      {dateTime && (
        <p className="text-xs text-muted-foreground mt-1 whitespace-nowrap">
          {dateTime}
          {relative && <> · {relative}</>}
        </p>
      )}
    </div>
  );
};

InterviewWhenCell.propTypes = {
  row: PropTypes.shape({
    interview_details: PropTypes.shape({
      interview_time: PropTypes.string,
      acknowledged_at: PropTypes.string,
      rejected_at: PropTypes.string,
      requires_acknowledgment: PropTypes.bool,
    }),
  }).isRequired,
};

export const interviewWhenColumn = {
  key: 'interview_when',
  header: 'When',
  className: 'text-sm',
  // Left-aligned per explicit request — every senior_level_flow column
  // reads left-to-right like the Candidate column, for a consistent
  // layout across tabs. The status pill is inline-flex (inline-level), so
  // it flows and left-aligns along with the date/time text below it.
  align: 'left',
  width: '220px',
  render: (_v, row) => <InterviewWhenCell row={row} />,
};

export default InterviewWhenCell;
